/**
 * 화면 위 디버깅 로그 오버레이 — 좌상단에 칩이 쌓이고 스스로 사라진다.
 *
 * 앱 웹뷰엔 개발자도구가 없어서 console.log 를 볼 수 없다. 그때 "지금 뭐가 오갔는지" 를
 * 화면으로 확인하려는 개발 도구다. **사용자에게 보여주는 알림이 아니다.**
 *
 * $j.Ui.Toast 와 나누어 둔 이유 —
 *   Toast : 사용자용. 1건만, 화면 중앙 상단, 사이트 테마·타이포를 따르고, 짧은 문장.
 *   여기  : 개발자용. 여러 건 스택, 모노스페이스, 원문 덤프(JSON), 기본 OFF 가능한 토글.
 *   섞으면 Toast 에 스택·큐·디버그 토글이 들어와 사용자 UI 쪽이 복잡해진다. 공유할 코드도
 *   'CSS 자가주입 + 타이머' 정도라 통합 이득이 작다.
 *
 * 사용 —
 *   $j.Ui.DebugLog.push('→ getDeviceContext 수락', ['{"code":"005"}', 'from: Android.start']);
 *   $j.Ui.DebugLog.push('실패', ['timeout'], { kind: 'error', timeout: 8000 });
 *   $j.Ui.DebugLog.clear();
 *
 * opts —
 *   kind    : 'info'(기본) | 'send' | 'recv' | 'warn' | 'error'  (좌측 색띠)
 *   timeout : 자동 소멸(ms). 기본 4000, 0 이면 자동 소멸 안 함 — 칩을 탭해서 지울 때까지 남는다
 *   tag     : console 접두어. 기본 'DebugLog'
 *
 * 조작 —
 *   칩을 아무거나 탭하면 **쌓인 전체가 즉시 사라진다** (한 건씩 지우는 건 손이 많이 간다).
 *   좌측의 작은 손잡이(‹/›)를 탭하면 **지우지 않고 접기/펼치기** — 화면을 계속 가리는 게
 *   방해될 때, 로그는 유지한 채로 잠깐 치웠다가 다시 꺼내 볼 수 있다.
 *   컨테이너는 pointer-events:none 이라 칩 바깥은 밑 화면이 그대로 눌린다.
 *
 * 켜기/끄기 — **기본 OFF**. 켜는 방법 3가지:
 *   URL 에 ?debugLog=1            (그 페이지 한 번만)
 *   $j.Ui.DebugLog.enabled = true (세션 유지 — 페이지를 옮겨도 켜져 있다. false 로 끔)
 *   window.JB_DEBUG_LOG = true    (그 페이지 한 번만, 콘솔에서 즉석 확인용)
 *   꺼져 있으면 console 출력도 하지 않는다 (끄면 완전히 조용하다).
 */
const MAX_CHIPS = 8;
const SS_KEY = 'app:ui:debugLog';   // 세션 토글 (탭 단위 — .ai/state-conventions.md §2 명명)

export default class DebugLog {
    constructor() {
        this.$wrap = null;
        this.$list = null;
        this.$handle = null;
    }

    /* ───────────────── public ───────────────── */

    /** 표시 여부 — 기본 OFF. 세션토글 · ?debugLog=1 · window.JB_DEBUG_LOG=true 중 하나라도 켜면 ON */
    get enabled() {
        if ($j.Common.Store.session.get(SS_KEY) === '1') return true;
        if (window.JB_DEBUG_LOG === true) return true;
        return /[?&]debugLog=1\b/.test(location.search);
    }

    /** 켜기/끄기 — 세션에 남으므로 페이지를 옮겨도 유지된다 */
    set enabled(v) {
        if (v) {
            $j.Common.Store.session.set(SS_KEY, '1');
        } else {
            $j.Common.Store.session.remove(SS_KEY);
            window.JB_DEBUG_LOG = false;
            this.clear();
        }
    }

    /**
     * 칩 1개 추가.
     * @param {string} title 첫 줄 (굵게)
     * @param {string[]} [lines] 이어붙일 줄들 — 빈 값은 알아서 걸러진다
     * @param {{kind?:string, timeout?:number, tag?:string}} [opts]
     */
    push = (title, lines = [], opts = {}) => {
        if (!this.enabled) return;   // 꺼져 있으면 console 도 남기지 않는다

        const body = (lines || []).filter(Boolean);
        console.log(`[${opts.tag || 'DebugLog'}] ${title} ${body.join(' ')}`);

        const kind = opts.kind || 'info';
        const html = `<b class="jb-dbg-t">${this._esc(title)}</b>`
                   + body.map(l => `<span>${this._esc(l)}</span>`).join('');

        this._getWrap();
        const $chip = $(`<div class="jb-dbg-chip is-${kind}">${html}</div>`).appendTo(this.$list);

        const $all = this.$list.children('.jb-dbg-chip');
        if ($all.length > MAX_CHIPS) $all.slice(0, $all.length - MAX_CHIPS).remove();

        const timeout = opts.timeout === 0 ? 0 : (opts.timeout || 4000);
        if (timeout > 0) {
            setTimeout(() => $chip.addClass('is-out'), Math.max(0, timeout - 350));
            setTimeout(() => $chip.remove(), timeout);
        }
    };

    /** 한 줄짜리 단축 호출 */
    log = (msg, opts = {}) => this.push(msg, [], opts);

    /** 쌓인 칩 전부 제거 (손잡이는 남는다 — 접기 상태·존재 자체는 지우기와 무관) */
    clear = () => {
        if (this.$list) this.$list.empty();
    };

    /* ───────────────── internal ───────────────── */

    /** 스택 컨테이너 (없으면 생성 + CSS 자가주입 — Toast.js 와 같은 방식) */
    _getWrap() {
        if (this.$wrap && this.$wrap.parent().length) return this.$wrap;

        this._injectStyle();
        this.$wrap = $('<div id="jb-debuglog"></div>').appendTo('body');
        this.$handle = $('<button type="button" class="jb-dbg-handle">‹</button>').appendTo(this.$wrap);
        this.$list = $('<div class="jb-dbg-list"></div>').appendTo(this.$wrap);

        // 아무 칩이나 탭 → 전체 제거 (위임이라 나중에 생긴 칩도 걸린다)
        this.$list.on('click', '.jb-dbg-chip', () => this.clear());
        // 손잡이 탭 → 지우지 않고 접기/펼치기
        this.$handle.on('click', () => this._toggleCollapsed());

        return this.$wrap;
    }

    /** 접기/펼치기 — 로그는 그대로 두고 화면만 가렸다 꺼냈다 한다 */
    _toggleCollapsed() {
        const collapsed = this.$wrap.toggleClass('is-collapsed').hasClass('is-collapsed');
        this.$handle.text(collapsed ? '›' : '‹');
    }

    _injectStyle() {
        if ($('#jb-debuglog-style').length) return;
        const css = `
#jb-debuglog{position:fixed;top:calc(env(safe-area-inset-top,0px) + 54px);left:6px;
  /* 6px 가 아니라 54px — 페이지 헤더의 뒤로가기 버튼(.hd .hb, 0~48px 영역)과 겹치지 않게
     헤더 높이(48px) 아래로 내린다 */
  z-index:calc(var(--z-index-loading,1080) + 5);display:flex;flex-direction:column;gap:4px;
  max-width:min(560px,calc(82vw / var(--adm-zoom, 1) / var(--fs-zoom, 1)));pointer-events:none;
  /* 안드로이드 웹뷰의 강제 다크(force-dark)가 이 오버레이를 다시 칠하지 않게 — 이미 다크다 */
  color-scheme:dark}
#jb-debuglog .jb-dbg-handle{pointer-events:auto;align-self:flex-start;width:22px;height:22px;
  border:none;border-radius:6px;background:#151515;color:#fff !important;
  font:14px/22px ui-monospace,Menlo,Consolas,monospace;text-align:center;padding:0;
  box-shadow:0 2px 8px rgba(0,0,0,.35)}
#jb-debuglog.is-collapsed .jb-dbg-list{display:none}
#jb-debuglog .jb-dbg-list{display:flex;flex-direction:column;gap:4px}
/* 글자색은 자식까지 !important 로 못박는다. 페이지 CSS(.xx b / .xx span 색 지정)가
   상속을 이기고 검은 글씨로 만들면 검은 배경 위에서 아무것도 안 보인다. */
#jb-debuglog .jb-dbg-chip,
#jb-debuglog .jb-dbg-chip *{color:#fff !important;-webkit-text-fill-color:#fff !important}
#jb-debuglog .jb-dbg-chip{pointer-events:auto;padding:6px 9px;border-radius:8px;
  display:flex;flex-direction:column;background:#151515;
  font:11px/1.45 ui-monospace,Menlo,Consolas,monospace;word-break:break-all;
  box-shadow:0 2px 8px rgba(0,0,0,.35);opacity:1;transition:opacity .3s}
#jb-debuglog .jb-dbg-chip.is-out{opacity:0}
#jb-debuglog .jb-dbg-t{font-weight:700}
#jb-debuglog .is-info{border-left:3px solid #9ca3af}
#jb-debuglog .is-send{border-left:3px solid #22c55e}
#jb-debuglog .is-recv{border-left:3px solid #38bdf8}
#jb-debuglog .is-warn{border-left:3px solid #f59e0b}
#jb-debuglog .is-error{border-left:3px solid #ef4444}
`;
        $('<style id="jb-debuglog-style">').text(css).appendTo('head');
    }

    _esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
}
